import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { DownloadSimple, X, Share, Plus } from "@phosphor-icons/react";
import {
    hasDeferredInstall, isStandalone, isIOS, subscribe, triggerInstall,
} from "../lib/installPrompt";

const DISMISS_KEY = "cf_install_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // a week

// Allow-list: only staff surfaces where installing the app as a quick-launch
// tool genuinely helps the people running events.
//   ✅ Admin dashboard + volunteer surfaces — staff who run events at the door.
//
// 🚫 Public donor/bidder pages (one-off visitors don't need to install an app).
// 🚫 Marketing / SaaS-shopping pages on charityfundraiser.com itself.
// 🚫 Big-screen modes (show/signage/scoreboard/kiosk).
// 🚫 Super-admin (internal). 🚫 Mid-transaction checkout.
const ALLOW_PREFIXES = [
    "/volunteer/", "/admin/", "/dashboard",
];

function isMobileDevice() {
    if (typeof navigator === "undefined") return false;
    const coarse = typeof window !== "undefined" && window.matchMedia
        ? window.matchMedia("(pointer: coarse)").matches : false;
    const ua = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");
    return coarse || ua;
}

function isAllowedPath(pathname) {
    if (!pathname) return false;
    return ALLOW_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

function recentlyDismissed() {
    try {
        const v = localStorage.getItem(DISMISS_KEY);
        if (!v) return false;
        return Date.now() - parseInt(v, 10) < DISMISS_TTL_MS;
    } catch { return false; }
}

/**
 * Auto-popup install prompt. Sits in the bottom-right corner of donor/staff
 * surfaces, then collapses after the user dismisses (7-day memory).
 */
export default function PWAInstallPrompt() {
    const { pathname } = useLocation();
    const [hasDeferred, setHasDeferred] = useState(hasDeferredInstall());
    const [iosVisible, setIosVisible] = useState(false);
    const [open, setOpen] = useState(false);

    // Subscribe to the singleton so we re-render when `beforeinstallprompt` fires.
    useEffect(() => subscribe(() => setHasDeferred(hasDeferredInstall())), []);

    useEffect(() => {
        if (!isAllowedPath(pathname)) { setOpen(false); return; }
        if (isStandalone() || recentlyDismissed()) return;

        if (hasDeferred) {
            setOpen(true);
            return;
        }
        if (isIOS()) {
            const t = setTimeout(() => { setIosVisible(true); setOpen(true); }, 4000);
            return () => clearTimeout(t);
        }
    }, [pathname, hasDeferred]);

    const install = useCallback(async () => {
        await triggerInstall();
        setOpen(false);
    }, []);

    const dismiss = useCallback(() => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
        setOpen(false);
    }, []);

    if (!open) return null;
    if (!isAllowedPath(pathname)) return null;
    const ios = iosVisible && !hasDeferred;

    return (
        <div
            data-testid="pwa-install-prompt"
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-[60] bg-white border-2 border-accent shadow-xl p-4"
            role="dialog"
            aria-live="polite"
        >
            <button
                onClick={dismiss}
                aria-label="Dismiss install prompt"
                className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
                data-testid="pwa-install-dismiss"
            >
                <X size={16} />
            </button>
            <p className="label-eyebrow text-[10px] text-accent">Install the app</p>
            {ios ? (
                <>
                    <p className="font-heading text-base text-primary mt-1 leading-snug">
                        Add CharityFundraiser to your Home Screen
                    </p>
                    <p className="text-xs text-muted-foreground mt-2 leading-snug">
                        Tap <Share size={14} className="inline -mt-0.5" weight="fill" /> in Safari, then choose{" "}
                        <span className="font-semibold text-foreground">Add to Home Screen</span>{" "}
                        <Plus size={12} className="inline -mt-0.5" />. Opens full-screen with no browser bar.
                    </p>
                </>
            ) : (
                <>
                    <p className="font-heading text-base text-primary mt-1 leading-snug">
                        {isMobileDevice() ? "Add CharityFundraiser to your phone" : "Install CharityFundraiser as a desktop app"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2 leading-snug">
                        {isMobileDevice()
                            ? "A one-tap app icon on your home screen — opens full-screen and keeps working even if Wi-Fi drops at the venue door. Ideal for volunteers running check-in."
                            : "A one-click icon in your taskbar/dock that opens in its own window (no browser tabs) and loads fast. Ideal for staff running events from a laptop."}
                    </p>
                    <button
                        onClick={install}
                        className="mt-3 w-full bg-primary text-primary-foreground py-2 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2"
                        data-testid="pwa-install-go"
                    >
                        <DownloadSimple size={14} weight="bold" /> {isMobileDevice() ? "Add to home screen" : "Install app"}
                    </button>
                </>
            )}
        </div>
    );
}
